import { sleep } from '@remote-voice/utilities';
import { Howler } from 'howler';
import Enumerable from 'linq';
import { useCallback, useEffect, useRef, useState } from 'react';

const useAudioInputTester = () => {
  const [runningDeviceId, setRunningDeviceId] = useState<string>();
  const volumeRef = useRef(0);
  const [denied, setDenied] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState<string | null>(null);

  // Monitor device changes
  useEffect(() => {
    const handleDeviceChange = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter((d) => d.kind === 'audioinput');
        const currentDeviceLabel = audioInputs[0]?.label;

        if (currentDeviceLabel !== deviceLabel) {
          setDeviceLabel(currentDeviceLabel);
        }
      } catch (error) {
        console.error('Error enumerating devices:', error);
      }
    };
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange
      );
    };
  }, [deviceLabel]);

  useEffect(() => {
    // マイクデバイス切替時、ボリューム計算を行う
    if (runningDeviceId != null) {
      let unmounted = false;
      const context = Howler.ctx;
      let stream: MediaStream | undefined;
      let workletNode: AudioWorkletNode | undefined;
      let sourceNode: MediaStreamAudioSourceNode | undefined;
      (async () => {
        try {
          // ソースノード取得
          stream = await navigator.mediaDevices.getUserMedia({
            //audio: true,
            audio: {
              deviceId: runningDeviceId
                ? { exact: runningDeviceId }
                : undefined,
            },
          });
          const track = stream.getAudioTracks()?.[0];
          setDeviceLabel(track?.label);
          if (unmounted) return;
          sourceNode = context.createMediaStreamSource(stream);

          // ワークレットからボリューム取得ノード生成
          await context.audioWorklet.addModule('/pcm-encode-audioworklet.js');
          if (unmounted) return;
          workletNode = new AudioWorkletNode(
            context,
            'PcmEncodeAudioWorkletProcessor'
          );
          let counter = 0;
          workletNode.port.onmessage = (ev: MessageEvent<Int16Array>) => {
            if (unmounted) return;
            counter = (counter + 1) % 10;
            if (counter > 0) return; //重いので適度に間引く
            const volume = Enumerable.from(ev.data)
              .take(50) // 重いので数サンプルのみ
              .select((x) => Math.abs(x) / 32768)
              .max(); // 波形の前数値の絶対値を0～1の範囲に収める
            volumeRef.current = Math.min(volume * 4000, 100); // ここの計算は適当
          };
          sourceNode.connect(workletNode); // ソースに接続
          setDenied(false);
        } catch (e: any) {
          console.error(e);
          setDenied(true);
          // cleanup
          workletNode?.port.postMessage('kill-process');
          workletNode?.disconnect();
          sourceNode?.disconnect();
          stream?.getTracks().forEach((track) => track.stop());
          // if (context.state !== 'closed') context.close();
        }
      })();
      return () => {
        // cleanup
        workletNode?.port.postMessage('kill-process');
        workletNode?.disconnect();
        sourceNode?.disconnect();
        stream?.getTracks().forEach((track) => track.stop());
        // if (context.state !== 'closed') context.close();
        unmounted = true;
      };
    }
  }, [runningDeviceId]);

  const start = useCallback(
    async (deviceId?: string) => {
      volumeRef.current = 0;
      if (runningDeviceId != null) {
        setRunningDeviceId(undefined);
        await sleep(0);
      }
      setRunningDeviceId(deviceId || ''); // `deviceId`を設定
    },
    [runningDeviceId]
  );

  const stop = useCallback(() => {
    setRunningDeviceId(undefined);
    volumeRef.current = 0;
  }, []);

  return {
    running: runningDeviceId != null,
    volume: volumeRef,
    denied,
    start,
    stop,
    deviceLabel,
  };
};
export default useAudioInputTester;
